import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import reportWebVitals from "./reportWebVitals";
import "./scss/styles.scss";
import "./i18n";

import * as Sentry from "@sentry/react";
import {
  captureClinicalProfileAccountInviteContext,
  captureClinicalProfileIntakeContext,
} from "./utils/clinicalProfileInviteContext";
import { sanitizeSentryEvent, scrubSentryBreadcrumb } from "./utils/sentryPrivacy";
import { installClientErrorFilter } from "./utils/clientErrorFilter";
import { captureImpersonationCode, redeemStagedImpersonationCode } from "./utils/impersonation";
import { reactRootOptions } from "./utils/reactRootOptions";

// Browser tracing reads window.location synchronously during Sentry.init.
// Move public intake and account-activation credentials into the current
// history entry first so a pageload transaction never observes a bearer URL.
captureClinicalProfileIntakeContext(undefined, { stageForRouter: true });
captureClinicalProfileAccountInviteContext(undefined, { stageForRouter: true });
// Same reasoning, for an admin-console "Enter as user" handover code.
captureImpersonationCode();

// Sentry PHI scrubber — runs on every event before send.
//
// The combination of sendDefaultPii + httpClientIntegration +
// captureConsoleIntegration({ levels: "error" }) means request/response
// bodies and any object passed to console.error CAN reach Sentry. For a
// patient session that includes shipping address, name, email, phone,
// order line items, clinical-profile fields — HIPAA-relevant. A
// patient-facing checkout error or list-fetch failure would otherwise
// generate an event tagged with the patient's email/id and a payload that
// carries identifying data.
//
// Scrubbing rules (defense-in-depth, not a substitute for backend
// audit-side PHI handling):
//  - Drop request/response bodies on authenticated /web calls and public
//    /api/v1/process-execute calls.
//  - Redact purpose credentials and contact PII from telemetry URLs.
//  - Drop Authorization / Cookie / Set-Cookie headers.
//  - Strip user.email; keep user.id for triage cohort grouping.
//  - Walk exception breadcrumbs and strip the same fields from any
//    captured request/response payload.
//  - Truncate very long error messages so a stringified body inside
//    err.message can't sneak the payload back in.
Sentry.init({
  dsn: "https://852edf5a3c66fa9117845bd14d8d1534@o4509943429922816.ingest.us.sentry.io/4509943431823360",
  // sendDefaultPii captures IP + user-agent. The beforeSend scrubber below
  // strips request/response bodies and email so this remains safe for
  // patient sessions where the request body would otherwise carry PHI.
  sendDefaultPii: true,
  enableLogs: true,
  environment: process.env.REACT_APP_SENTRY_ENV,
  attachStacktrace: true,
  normalizeDepth: 3,
  beforeSend: sanitizeSentryEvent,
  beforeSendTransaction: sanitizeSentryEvent,
  beforeBreadcrumb: (breadcrumb) => scrubSentryBreadcrumb(breadcrumb),
  integrations: [
    Sentry.browserSessionIntegration(),
    Sentry.contextLinesIntegration(),
    Sentry.extraErrorDataIntegration(),
    Sentry.functionToStringIntegration(),
    Sentry.httpClientIntegration(),
    Sentry.linkedErrorsIntegration(),
    Sentry.browserTracingIntegration(),
    Sentry.captureConsoleIntegration({
      levels: "error",
    }),
  ],
  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for performance monitoring.
  // We recommend adjusting this value in production
  tracesSampleRate: 1.0,
  // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
  // tracePropagationTargets: ["localhost", /^https:\/\//, /lucashealth.ai/, /api/],
  // Set profilesSampleRate to 1.0 to profile every transaction.
  // Since profilesSampleRate is relative to tracesSampleRate,
  // the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
  // For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
  // results in 25% of transactions being profiled (0.5*0.5=0.25)
  profilesSampleRate: 1.0,
});

// Classify console.error before it becomes an alert. Installed AFTER Sentry.init so it
// wraps Sentry's own console wrapper: an expected failure (expired session, dropped
// connection) becomes a breadcrumb instead of an event, and — in the mobile WebView,
// where the native shell forwards every console.error into an ArcadeBrain org event
// that mails support@ — is not forwarded at all. See utils/clientErrorFilter.js.
installClientErrorFilter();

// Suppress benign ResizeObserver error (triggered by React Flow and other resize-aware libs)
window.addEventListener("error", (e) => {
  if (e.message === "ResizeObserver loop completed with undelivered notifications.") {
    e.stopImmediatePropagation();
  }
});

// reactRootOptions: see utils/reactRootOptions.js -- React's default onCaughtError
// console.errors a boundary-caught error before the boundary runs, which let the
// console integration steal the capture from the Sentry ErrorBoundary.
const root = ReactDOM.createRoot(document.getElementById("root"), reactRootOptions);
// Redeem a staged "Enter as user" handover before the first render, so the app boots already
// signed in as the impersonated user instead of flashing the normal login screen first.
// Redeem a staged "Enter as user" handover before the first render, so the app boots already signed
// in as the impersonated user instead of flashing the login screen. The result is an ordinary
// session, so everything after this point is the normal bootstrap.
redeemStagedImpersonationCode().then(() => {
  root.render(<App />);
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
